"use client"

import * as React from "react"
import { ArrowUp } from "lucide-react"

interface BackToTopButtonProps {
  /** Scroll threshold in px before the button fades in. Default 600. */
  threshold?: number
  /** Optional class on the button itself for nudging position. */
  className?: string
  /**
   * When set, the button scrolls to the element with this `id` instead of
   * the page top. Used on `/brands` so the mobile-only "back to filters"
   * tap returns the visitor to the toolbar rather than the hero. The
   * `aria-label` updates to reflect the target.
   */
  anchorId?: string
  /** Override the aria-label / title (default derived from `anchorId`). */
  label?: string
}

/**
 * Fixed bottom-right pill that fades in once the visitor scrolls past
 * `threshold` and smooth-scrolls them to either the page top (default) or
 * the element identified by `anchorId`.
 *
 * Brand-kit citizen because long landing pages (brands index, blog
 * archive, hub deep dives) all benefit from a return path. Anchor
 * targeting lets callers pin the return-point to a section like the
 * search/filter toolbar so the user lands back at the affordance they
 * came from instead of the very top of the page.
 */
export function BackToTopButton({
  threshold = 600,
  className = "",
  anchorId,
  label,
}: BackToTopButtonProps) {
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [threshold])

  const onClick = React.useCallback(() => {
    if (anchorId) {
      const el = document.getElementById(anchorId)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
        return
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [anchorId])

  const ariaLabel = label ?? (anchorId ? "Back to filters" : "Back to top")

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={`fixed right-6 bottom-6 z-40 grid h-11 w-11 place-items-center rounded-full bg-gray-900 text-white shadow-[0_8px_24px_-8px_rgba(0,0,0,0.3)] transition-opacity duration-200 hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951] focus-visible:ring-offset-2 ${
        visible ? "opacity-100" : "pointer-events-none opacity-0"
      } ${className}`}
    >
      <ArrowUp className="h-[18px] w-[18px]" aria-hidden />
    </button>
  )
}
